// Utility functions for managing recently logged in accounts

export interface RecentAccount {
  id: string
  email: string
  firstName?: string
  lastName?: string
  profilePictureUrl?: string
  lastLoginAt: string
  hasPasskey?: boolean  // Legacy field - kept for backward compatibility
  preferredLoginMethod?: 'passkey' | 'sms_2fa' | 'totp_2fa'  // New field for preferred 2FA method
}

const RECENT_ACCOUNTS_KEY = 'recentlyLoggedInAccounts'
const MAX_RECENT_ACCOUNTS = 3

export const getRecentAccounts = (): RecentAccount[] => {
  try {
    const stored = localStorage.getItem(RECENT_ACCOUNTS_KEY)
    if (!stored) return []
    
    const accounts = JSON.parse(stored) as RecentAccount[]
    
    // Sort by most recent login
    return accounts.sort((a, b) => 
      new Date(b.lastLoginAt).getTime() - new Date(a.lastLoginAt).getTime()
    ).slice(0, MAX_RECENT_ACCOUNTS)
  } catch (error) {
    console.error('Error reading recent accounts:', error)
    return []
  }
}

export const addRecentAccount = (account: Omit<RecentAccount, 'lastLoginAt'>): void => {
  try {
    const currentAccounts = getRecentAccounts()
    
    // Remove existing account with same email if exists
    const filteredAccounts = currentAccounts.filter(acc => acc.email !== account.email)
    
    // Add new account at the beginning
    const newAccount: RecentAccount = {
      ...account,
      lastLoginAt: new Date().toISOString()
    }
    
    const updatedAccounts = [newAccount, ...filteredAccounts].slice(0, MAX_RECENT_ACCOUNTS)
    
    localStorage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(updatedAccounts))
  } catch (error) {
    console.error('Error saving recent account:', error)
  }
}

export const removeRecentAccount = (email: string): void => {
  try {
    const currentAccounts = getRecentAccounts()
    const filteredAccounts = currentAccounts.filter(acc => acc.email !== email)
    
    localStorage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(filteredAccounts))
  } catch (error) {
    console.error('Error removing recent account:', error)
  }
}

export const clearRecentAccounts = (): void => {
  try {
    localStorage.removeItem(RECENT_ACCOUNTS_KEY)
  } catch (error) {
    console.error('Error clearing recent accounts:', error)
  }
}

export const updateRecentAccountPasskeyStatus = (email: string, hasPasskey: boolean): void => {
  try {
    const currentAccounts = getRecentAccounts()
    const updatedAccounts = currentAccounts.map(acc => 
      acc.email === email ? { ...acc, hasPasskey } : acc
    )
    
    localStorage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(updatedAccounts))
  } catch (error) {
    console.error('Error updating recent account passkey status:', error)
  }
}
